import {PlusIcon, XMarkIcon} from '@heroicons/react/24/solid';
import React from 'react';

import {recordEvent} from 'src/components/Analytics';
import AddModelMenu from 'src/components/h5/AddModelMenu';
import {ModelInfoTooltip, VisibilityBadge} from 'src/components/h5/ModelLabels';
import {LegendConfig} from 'src/components/Legend';
import {useTranslations} from 'src/components/TranslationsContext';
import {H5ForecastListing} from 'src/lib/api/h5';

export type H5LegendModel = {
  listing: H5ForecastListing;
  // The legend group the forecast's series are under.
  name: string;
  removable: boolean;
  // The newest run drawn and the horizon, formatted; every run drawn, listed for a tooltip.
  details?: string;
  allInitTimes?: string;
  // Set when a compared forecast had no run to pair, in place of the run details.
  noRunNote?: string;
  // Its rows are still being read, so there is nothing to toggle yet.
  loading?: boolean;
};

type Toggle = (ids: string[], nextValue: boolean) => void;

type Props = {
  configs: LegendConfig[];
  isVisibleById: Record<string, boolean>;
  onChange: Toggle;
  models: H5LegendModel[];
  showModelInfo: boolean;
  onRemove: (listing: H5ForecastListing) => void;
  addable: {
    listings: H5ForecastListing[];
    nameOf: (listing: H5ForecastListing) => string;
    onAdd: (listing: H5ForecastListing) => void;
  };
};

const ICON_SIZE = 16;

export function RemoveModelButton({onClick}: {onClick: () => void}) {
  const {t} = useTranslations();
  return (
    <button
      type="button"
      aria-label={t('removeForecast')}
      title={t('removeForecast')}
      className="-m-1 inline-flex shrink-0 p-1 align-middle text-gray-400 hover:text-gray-700"
      onClick={onClick}
    >
      <XMarkIcon className="h-4 w-4" />
    </button>
  );
}

// A series toggle: filled while the series is drawn, outlined while it is hidden.
function Chip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`rounded border px-2 py-0.5 text-xs ${
        on ? 'bg-gray-200 text-gray-900 border-gray-200' : 'bg-white text-gray-500 border-gray-200'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function ChipIcon({item}: {item: LegendConfig}) {
  return (
    <svg
      width={10}
      height={10}
      viewBox={`0 0 ${ICON_SIZE} ${ICON_SIZE}`}
      className="shrink-0"
      aria-hidden
    >
      {item.makeIcon(-1, 0)}
    </svg>
  );
}

function Swatch({lines, bands}: {lines: LegendConfig[]; bands: LegendConfig[]}) {
  const line = lines[0] ?? bands[0];
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} className="shrink-0">
      {line ? (
        // The CI fill behind the line: a 2px line alone is hard to tell apart by color.
        <>
          {bands.map((band) => (
            <React.Fragment key={band.id}>{band.makeIcon(-1, 0)}</React.Fragment>
          ))}
          {line.makeIcon(-1, 0)}
        </>
      ) : (
        // Hatched: a place in the palette with nothing drawn in it.
        <>
          <rect
            x={0.5}
            y={0.5}
            width={ICON_SIZE - 1}
            height={ICON_SIZE - 1}
            rx={3}
            ry={3}
            fill="#f3f4f6"
            stroke="#d1d5db"
          />
          <line x1={3} y1={ICON_SIZE - 3} x2={ICON_SIZE - 3} y2={3} stroke="#9ca3af" />
        </>
      )}
    </svg>
  );
}

function ModelRow({
  model,
  items,
  isVisibleById,
  toggle,
  showModelInfo,
  onRemove,
}: {
  model: H5LegendModel;
  items: LegendConfig[];
  isVisibleById: Record<string, boolean>;
  toggle: Toggle;
  showModelInfo: boolean;
  onRemove: (listing: H5ForecastListing) => void;
}) {
  const {t} = useTranslations();
  const muted = !!model.noRunNote;
  // A row with no run to pair draws nothing, so its swatch is blank.
  const shown = muted ? [] : items;
  const bands = shown.filter((item) => item.legendIcon === 'rect');
  const lines = shown.filter((item) => !bands.includes(item));
  const bandsVisible = bands.every((band) => isVisibleById[band.id]);
  const nothingDrawn = items.length > 0 && items.every((item) => !isVisibleById[item.id]);
  const [title, ...rest] = model.name.split(' · ');
  const descriptor = rest.length > 0 ? rest.join(' · ') : undefined;
  const {visibility} = model.listing;
  const details = model.noRunNote ?? (model.loading ? t('loading') : model.details);
  return (
    <div
      data-legend-model={model.name}
      // Names the row's chips and remove button, which are alike on every row.
      role="group"
      aria-label={model.name}
      data-export-omit={nothingDrawn || undefined}
      className="flex min-h-12 items-center gap-3 px-2 py-1.5"
    >
      <Swatch lines={lines} bands={bands} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className={`font-semibold ${muted ? 'text-gray-400' : 'text-gray-900'}`}>
            {title}
          </span>
          {descriptor && (
            <span className={muted ? 'text-gray-400' : 'text-gray-500'}>{descriptor}</span>
          )}
          {visibility !== 'secondary' && <VisibilityBadge visibility={visibility} />}
          {showModelInfo && <ModelInfoTooltip listing={model.listing} />}
        </div>
        {details && (
          <div className={muted ? 'text-amber-600' : 'text-gray-500'} title={model.allInitTimes}>
            {details}
          </div>
        )}
      </div>
      {!muted && !model.loading && (
        <div className="ml-auto flex shrink-0 gap-1">
          {lines.map((line) => (
            <Chip
              key={line.id}
              on={isVisibleById[line.id]}
              onClick={() => toggle([line.id], !isVisibleById[line.id])}
            >
              <span className="flex items-center gap-1">
                <ChipIcon item={line} />
                {line.shortDisplayName ?? line.display}
              </span>
            </Chip>
          ))}
          {bands.length > 0 && (
            <Chip
              on={bandsVisible}
              onClick={() =>
                toggle(
                  bands.map((band) => band.id),
                  !bandsVisible
                )
              }
            >
              {/* Each band's shade by its level, so the chart reads without hovering, in an export too. */}
              <span className="flex items-center gap-2">
                {bands.map((band) => (
                  <span key={band.id} className="flex items-center gap-1">
                    <ChipIcon item={band} />
                    {band.shortDisplayName ?? band.display}
                  </span>
                ))}
              </span>
            </Chip>
          )}
        </div>
      )}
      {model.removable ? (
        <RemoveModelButton onClick={() => onRemove(model.listing)} />
      ) : (
        <span className="inline-block h-4 w-4 shrink-0" aria-hidden />
      )}
    </div>
  );
}

/**
 * The H5 flow chart's legend: the observation toggles, a row per forecast drawn with its run
 * details and series chips, and the button that adds a forecast. Hiding a series goes through the
 * chart's visibility state like the shared legend; adding and removing forecasts is the section's.
 */
export default function H5FlowLegend({
  configs,
  isVisibleById,
  onChange,
  models,
  showModelInfo,
  onRemove,
  addable,
}: Props) {
  const {t} = useTranslations();
  const modelNames = new Set(models.map((model) => model.name));
  const itemsByModel = new Map<string, LegendConfig[]>();
  const observations: LegendConfig[] = [];
  for (const config of configs) {
    if (config.group && modelNames.has(config.group)) {
      itemsByModel.set(config.group, [...(itemsByModel.get(config.group) ?? []), config]);
    } else {
      observations.push(config);
    }
  }
  const toggle: Toggle = (ids, nextValue) => {
    onChange(ids, nextValue);
    if (ids.length === 1) recordEvent('legend_item_clicked', {id: ids[0]});
    else recordEvent('legend_group_clicked', {ids: ids.join(',')});
  };
  // A forecast is listed once the chart has something of it to toggle, while its rows load, or
  // when it tells why it has nothing; one that failed is reported above the chart instead.
  const rows = models.filter(
    (model) => (itemsByModel.get(model.name)?.length ?? 0) > 0 || model.loading || model.noRunNote
  );

  return (
    <div className="text-xs bg-white pt-2 pb-4">
      {observations.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 px-2 pb-2">
          {observations.map(({id, display, makeIcon}) => {
            const isVisible = isVisibleById[id];
            return (
              <button
                key={id}
                type="button"
                data-legend-id={id}
                aria-pressed={isVisible}
                className={`flex items-center gap-1.5 ${isVisible ? 'text-gray-800' : 'text-gray-400'}`}
                onClick={() => toggle([id], !isVisible)}
              >
                <svg width={ICON_SIZE} height={ICON_SIZE}>
                  {makeIcon(-1, 0)}
                </svg>
                {display}
              </button>
            );
          })}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
        {rows.map((model) => (
          <ModelRow
            key={model.name}
            model={model}
            items={itemsByModel.get(model.name) ?? []}
            isVisibleById={isVisibleById}
            toggle={toggle}
            showModelInfo={showModelInfo}
            onRemove={onRemove}
          />
        ))}
        {addable.listings.length > 0 && (
          <AddModelMenu
            listings={addable.listings}
            nameOf={addable.nameOf}
            showModelIds={showModelInfo}
            onAdd={addable.onAdd}
            // The same height as a forecast row, so the cell lines up with the row beside it. It
            // stays in the right column so it doesn't jump sides as forecasts are added.
            wrapperClassName={rows.length > 0 ? 'md:col-start-2' : undefined}
            className="flex min-h-12 w-full items-center gap-2 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-left"
          >
            <PlusIcon className="h-4 w-4 text-blue-600" />
            <span className="text-blue-600">{t('addForecast')}</span>
            <span className="text-gray-500">
              {t('moreForecastsAvailable', {count: String(addable.listings.length)})}
            </span>
          </AddModelMenu>
        )}
      </div>
    </div>
  );
}
